/**
 * Abschnitt `document-comments` im Blatt „Dokument ändern" (Spec 44, nur
 * `isEdit`). Kommentare werden **sofort** gesendet und gelöscht — das Formular
 * bleibt dabei unberührt (Fachregel 8). Kürzel, Zeitpunkt und alle
 * Fehlertexte kommen wörtlich vom Server.
 *
 * Reine Darstellung samt lokalem Eingabezustand: Die Abfrage und die beiden
 * Mutationen gehören dem Container (`DocumentSheet`), hier kommen sie als
 * Props herein.
 *
 * **⌘↩ / Strg+↩ im Feld sendet den Kommentar, nicht das Formular** (Fachregel
 * 10): `DocumentSheet` bindet dieselbe Kombination global an Speichern. Das
 * Feld hält das Ereignis deshalb an, bevor es das Fenster erreicht.
 */

import { useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'

import { FIELD_CLASS } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import type { DocumentComment, DocumentComments as DocumentCommentsData } from '@/features/documents/api'

interface DocumentCommentsProps {
  /** `undefined`, solange die Antwort fehlt. Bleibt bei einem späteren
   * Hintergrundfehler stehen — dann zählt der letzte gute Stand. */
  readonly data: DocumentCommentsData | undefined
  readonly isError: boolean
  readonly onRetry: () => void
  /** Wirft bei Fehlern; `idempotencyKey` bleibt für denselben Text gleich. */
  readonly onSend: (body: string, idempotencyKey: string) => Promise<unknown>
  readonly onDelete: (commentId: string) => Promise<unknown>
}

/** Reiner UI-Text: welche Taste sendet. */
function sendHint(): string {
  return /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘↩ sendet' : 'Strg+↩ sendet'
}

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.'
}

function CommentsHead({ count }: { readonly count: number }) {
  return (
    <h3 id="document-comments-title" className="text-body text-text flex items-baseline gap-1.5 font-semibold">
      Kommentare
      {count > 0 && (
        <span data-testid="document-comments-count" className="text-muted font-normal">
          {count}
        </span>
      )}
    </h3>
  )
}

function CommentsLoading() {
  return (
    <div data-testid="document-comments-loading" aria-busy="true" className="flex flex-col">
      {[0, 1].map((index) => (
        <div key={index} className={`flex gap-2.5 py-2.5 ${index > 0 ? 'border-border border-t' : ''}`}>
          <span className="bg-border size-6 shrink-0 animate-pulse rounded-full" />
          <div className="flex flex-1 flex-col gap-2 pt-0.5">
            <span className="bg-border h-3 w-2/5 animate-pulse rounded-card" />
            <span className="bg-border h-3 w-4/5 animate-pulse rounded-card" />
          </div>
        </div>
      ))}
    </div>
  )
}

interface CommentRowProps {
  readonly comment: DocumentComment
  readonly isFirst: boolean
  readonly isDeleting: boolean
  readonly onDelete: (commentId: string) => void
}

function CommentRow({ comment, isFirst, isDeleting, onDelete }: CommentRowProps) {
  const { id } = comment
  return (
    <li data-testid={`document-comment-${id}`} className={`flex gap-2.5 py-2.5 ${isFirst ? '' : 'border-border border-t'}`}>
      <span
        aria-hidden="true"
        data-testid={`document-comment-author-${id}`}
        className="bg-border text-text text-label grid size-6 shrink-0 place-items-center rounded-full font-semibold"
      >
        {comment.author_initials}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-label text-muted tabular-nums">
            <b className="text-text mr-1.5 font-semibold">{comment.author_initials}</b>
            <span data-testid={`document-comment-time-${id}`}>{comment.created_at_display}</span>
          </span>
          {comment.can_delete && (
            <button
              type="button"
              data-testid={`document-comment-delete-${id}`}
              aria-label={`Kommentar vom ${comment.created_at_display} löschen`}
              disabled={isDeleting}
              onClick={() => {
                onDelete(id)
              }}
              className="text-label text-muted hover:text-status-overdue hover:bg-status-overdue-surface focus-visible:text-status-overdue focus-visible:bg-status-overdue-surface rounded-card -my-2.5 px-2 transition-colors duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
            >
              Löschen
            </button>
          )}
        </div>
        <p data-testid={`document-comment-body-${id}`} className="text-body text-text break-words whitespace-pre-wrap">
          {comment.body}
        </p>
      </div>
    </li>
  )
}

export function DocumentComments({ data, isError, onRetry, onSend, onDelete }: DocumentCommentsProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [isSending, setIsSending] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // Gleicher Text → gleicher Schlüssel: Ein Wiederholen nach Abbruch der
  // Verbindung legt keinen zweiten Kommentar (und keinen zweiten Push) an.
  const keyRef = useRef<{ readonly text: string; readonly key: string } | null>(null)

  const canSend = draft.trim() !== '' && !isSending

  async function send() {
    if (!canSend) return
    if (keyRef.current?.text !== draft) keyRef.current = { text: draft, key: crypto.randomUUID() }
    setIsSending(true)
    setError(undefined)
    try {
      await onSend(draft, keyRef.current.key)
      setDraft('')
      keyRef.current = null
      inputRef.current?.focus()
    } catch (sendError) {
      setError(errorText(sendError))
    } finally {
      setIsSending(false)
    }
  }

  async function remove(commentId: string) {
    setDeletingId(commentId)
    setError(undefined)
    try {
      await onDelete(commentId)
    } catch (deleteError) {
      setError(errorText(deleteError))
    } finally {
      setDeletingId(null)
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (!(event.metaKey || event.ctrlKey) || event.key !== 'Enter' || event.nativeEvent.isComposing) return
    // Weder Formular noch der globale Speichern-Handler des Blatts (Fachregel 10).
    event.preventDefault()
    event.stopPropagation()
    void send()
  }

  const errorLine =
    error === undefined ? null : (
      <p data-testid="document-comment-error" role="alert" className="text-label text-status-overdue">
        {error}
      </p>
    )
  const items = data?.items ?? []
  const canComment = data?.can_comment === true

  return (
    <section data-testid="document-comments" aria-labelledby="document-comments-title" className="border-border flex flex-col gap-1 border-t pt-4">
      <CommentsHead count={items.length} />

      {data === undefined && isError && (
        <div data-testid="document-comments-error" className="flex flex-wrap items-center justify-between gap-2 py-1">
          <span className="text-body text-muted">Kommentare nicht geladen.</span>
          <button
            type="button"
            data-testid="document-comments-retry"
            onClick={onRetry}
            className={`text-text px-4 ${FIELD_CLASS}`}
          >
            Erneut laden
          </button>
        </div>
      )}

      {data === undefined && !isError && <CommentsLoading />}

      {data !== undefined && (
        <>
          {items.length === 0 ? (
            <p data-testid="document-comments-empty" className="text-body text-muted pt-1">
              Noch keine Kommentare.
            </p>
          ) : (
            <ul className="flex flex-col">
              {items.map((comment, index) => (
                <CommentRow
                  key={comment.id}
                  comment={comment}
                  isFirst={index === 0}
                  isDeleting={deletingId === comment.id}
                  onDelete={(commentId) => void remove(commentId)}
                />
              ))}
            </ul>
          )}

          {canComment && (
            <div className="flex flex-col gap-2 pt-2">
              <textarea
                ref={inputRef}
                data-testid="document-comment-input"
                aria-label="Kommentar"
                placeholder="Kommentar schreiben …"
                rows={3}
                maxLength={2000}
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value)
                }}
                onKeyDown={handleKeyDown}
                className={`text-text placeholder:text-muted w-full resize-none py-2 ${FIELD_CLASS}`}
              />
              {errorLine}
              <div className="flex items-center justify-between gap-2">
                <span className="text-label text-muted">{sendHint()}</span>
                <button
                  type="button"
                  data-testid="document-comment-send"
                  disabled={!canSend}
                  aria-busy={isSending}
                  onClick={() => void send()}
                  className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-45"
                >
                  Kommentieren
                </button>
              </div>
            </div>
          )}
          {!canComment && errorLine}
        </>
      )}
    </section>
  )
}
